import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate } from '~/types/dates'
import type { ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssue, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * The lightweight daily observations (D-096, pending).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS RECORDS WHETHER A TARGET WAS MET. IT NEVER RECORDS A QUANTITY.
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  There is no field here for calories consumed, protein grams, a food, a meal
 *  or a macronutrient, and adding one needs a new decision rather than a new
 *  field. The whole surface is three taps: was I within my calorie target, did I
 *  reach my protein target, did I do the session. Numeric daily intake was
 *  considered and rejected — it puts a keyboard on the app's most
 *  abandonment-prone screen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  'unsure', ABSENT, AND WHY THEY ARE BOTH MISSING RATHER THAN 'no'
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A missing value is never a zero, and at this scale that means a day the user
 *  did not answer is **not a day they failed**. `'unsure'` is a real answer with
 *  the same evidential weight as no answer at all: it says the user cannot tell,
 *  which is different from saying no. Both are omitted from a weekly count
 *  rather than counted against it, and neither renders as a failure.
 *
 *  This is why the field type has three values and not two. A boolean would have
 *  forced `false` to carry both "no" and "I don't know", and the count derived
 *  from it would have been quietly wrong in the direction that scolds the user.
 */

export const TERNARY_ANSWERS = ['yes', 'no', 'unsure'] as const

export type TernaryAnswer = (typeof TERNARY_ANSWERS)[number]

export const TERNARY_ANSWER_LABELS: Readonly<Record<TernaryAnswer, string>> = {
  yes: 'Yes',
  no: 'No',
  unsure: 'Not sure',
}

export function isTernaryAnswer(value: string): value is TernaryAnswer {
  return (TERNARY_ANSWERS as readonly string[]).includes(value)
}

/**
 * Whether an answer counts towards a weekly total.
 *
 * The single place this question is answered, so a later count cannot decide it
 * differently. `'unsure'` and `undefined` are both excluded — see the note above.
 */
export function isJudgeableAnswer(answer: TernaryAnswer | undefined): boolean {
  return answer === 'yes' || answer === 'no'
}

/** Whether an answer is a recorded "target met". */
export function isMet(answer: TernaryAnswer | undefined): boolean {
  return answer === 'yes'
}

export interface NutritionDailyCheck {
  readonly calorieTarget?: TernaryAnswer
  readonly proteinTarget?: TernaryAnswer
  /** Asked instead of the two above when the plan carries no numbers. */
  readonly followedPlan?: TernaryAnswer
}

/**
 * `SessionDailyCheck` and `sessionDone` were deleted at C7.5 (D-105 §12).
 *
 * They existed from C6.0 and **nothing ever wrote them**, because a week was a
 * quota rather than a schedule: `PlannedItem` could say *three sessions a week*
 * and never *Monday's Upper Body session*, so there was no subject for the
 * question and every commitment line on Today read "0 recorded, 3 planned" for
 * as long as the field existed.
 *
 * A session is now recorded against the materialised week (`trainingWeek.ts`),
 * where it has an identity, a date and an outcome that can be corrected. **Do
 * not reintroduce a per-day session flag here**: it would be a second
 * representation of the same fact, and the day would disagree with the week the
 * first time one of them was corrected.
 *
 * The deletion needs no migration and `dailyCheckStoredSessionFields` proves it
 * rather than asserting it — this project has been caught before by an invariant
 * about an empty store that nothing implemented.
 */

/**
 * The session fields this record no longer carries.
 *
 * Exported for the test that reads stored records and confirms none of them
 * holds one. A `const` list rather than three strings in a test, so a field
 * removed later is added here and checked by the same guard.
 */
export const REMOVED_SESSION_FIELDS = ['cardio', 'mobility', 'strength'] as const

/**
 * One calendar day's observations.
 *
 * Keyed by the date itself, following `weightEntries` (D-027, D-035): one answer
 * per day becomes structural rather than a validation rule, and correcting a day
 * is an overwrite rather than a second record competing with the first. The
 * trailing-week read is one bounded `IDBKeyRange` scan.
 *
 * Every domain block is optional and an absent block means the question was not
 * asked, which is what happens for a domain the user has not activated. A day
 * with no record at all is an ordinary, unremarkable day.
 */
export interface DailyCheckRecord extends RecordEnvelope {
  readonly date: CalendarDate
  readonly projectId?: ProjectId
  readonly nutrition?: NutritionDailyCheck
}

/** A partial update. Only the blocks named are touched. */
export interface DailyCheckPatch {
  readonly nutrition?: NutritionDailyCheck
  readonly projectId?: ProjectId
}

export function validateDailyCheckPatch(patch: DailyCheckPatch): ValidationIssues {
  const issues: ValidationIssue[] = []

  const nutrition = patch.nutrition
  if (nutrition !== undefined) {
    for (const [field, value] of Object.entries(nutrition)) {
      if (value !== undefined && !isTernaryAnswer(value as string)) {
        issues.push(
          issue(`nutrition.${field}`, `Answer must be one of: ${TERNARY_ANSWERS.join(', ')}.`),
        )
      }
    }
    if (nutrition.followedPlan !== undefined && nutrition.calorieTarget !== undefined) {
      /* A plan is either numeric or it is not. Recording both shapes for one day
         means two different questions were asked about the same thing, and the
         weekly count would have to choose between them. */
      issues.push(
        issue(
          'nutrition',
          'A day carries either the target answers or the followed-plan answer, not both.',
        ),
      )
    }
  }

  return issues
}

/** Build a day's record from nothing. */
export function buildDailyCheckRecord(
  date: string,
  patch: DailyCheckPatch,
  now: IsoTimestamp,
): Result<DailyCheckRecord, ValidationIssues> {
  const issues = validateDailyCheckPatch(patch)
  if (issues.length > 0) return err(issues)

  const calendarDate = toCalendarDate(date)
  if (calendarDate === undefined) {
    return err([issue('date', 'Date must be a real date in YYYY-MM-DD form.')])
  }

  return ok({
    date: calendarDate,
    ...blocksFrom(patch),
    createdAt: now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * Merge a patch into an existing day.
 *
 * Blocks not named in the patch are preserved, and a named block **replaces**
 * rather than merges field by field. That is deliberate: the interface presents
 * nutrition as one small group of questions answered together, so a patch
 * carrying `{ calorieTarget: 'yes' }` after a day that also had a protein answer
 * is the user re-answering that group, not amending one field of it. Field-level
 * merging would make it impossible to ever clear an answer.
 */
export function applyDailyCheckPatch(
  record: DailyCheckRecord,
  patch: DailyCheckPatch,
  now: IsoTimestamp,
): Result<DailyCheckRecord, ValidationIssues> {
  const issues = validateDailyCheckPatch(patch)
  if (issues.length > 0) return err(issues)

  return ok({
    ...record,
    ...blocksFrom(patch),
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

function blocksFrom(patch: DailyCheckPatch): Partial<DailyCheckRecord> {
  return {
    ...(patch.projectId === undefined ? {} : { projectId: patch.projectId }),
    ...(patch.nutrition === undefined ? {} : { nutrition: patch.nutrition }),
  }
}

/** Whether this record holds any answer at all. */
export function hasAnyAnswer(record: DailyCheckRecord): boolean {
  /* Nutrition alone since C7.5: a session is recorded against the materialised
     week, which is not a day's observation and is not read from here. */
  const answers = [
    record.nutrition?.calorieTarget,
    record.nutrition?.proteinTarget,
    record.nutrition?.followedPlan,
  ]
  return answers.some((answer) => answer !== undefined)
}

/**
 * A weekly count derived from a week's days.
 *
 * `daysJudgeable` is the denominator that matters: a week with two answered days
 * out of seven has a numerator of at most two, and reporting `2/7` would read as
 * five failures. `daysUnknown` is carried so a caller can say "you answered two
 * days" rather than implying anything about the other five.
 */
export interface DailyCheckTally {
  readonly daysRecorded: number
  readonly daysJudgeable: number
  readonly daysMet: number
  readonly daysUnknown: number
}

/**
 * Tally one field across a week's records.
 *
 * Pure and total: `daysUnknown` counts days present but unanswerable, and days
 * with no record at all are simply not in the input. Nothing here divides, and
 * nothing here produces a percentage — that is the caller's decision, and the
 * anti-scoring rule means most callers should not.
 */
export function tally(
  records: readonly DailyCheckRecord[],
  pick: (record: DailyCheckRecord) => TernaryAnswer | undefined,
): DailyCheckTally {
  let daysJudgeable = 0
  let daysMet = 0
  let daysUnknown = 0

  for (const record of records) {
    const answer = pick(record)
    if (isJudgeableAnswer(answer)) {
      daysJudgeable += 1
      if (isMet(answer)) daysMet += 1
    } else {
      daysUnknown += 1
    }
  }

  return { daysRecorded: records.length, daysJudgeable, daysMet, daysUnknown }
}
